import { createInstance } from '@amplitude/analytics-browser';
import { DefaultConfiguration, ampli } from '../../ampli/index';

import { fplsFetch, getActiveReactPage, getEnvironment } from './utils';

export const resetAmplitudeSession = () => ampli.flush();

const getFormIdFromUrl = () => {
  const path = window.location.pathname;
  const match = path.match(/^\/r\/form\/(\d+)(\/|$)/);
  return match ? match[1] : null;
};

/**
 * This plugin enriches events by updating necessary event properties.
 *
 * @returns EnrichmentPlugin
 */
export const trackingEnrichmentPlugin = () => ({
  name: 'page-view-tracking-enrichment',
  setup: async () => undefined,
  type: 'enrichment',
  execute: async (event) => {
    if (event.event_type === '[Amplitude] Page Viewed') {
      // The enrichment is necessary because Amplitude page view event runs 
      // before react-helmet's page title is updated, leading to stale
      // page title in the event properties object.
      
      const page = getActiveReactPage();

      // Customize page view events to make sense when performing analytics.
      event.event_type = `Viewed ${page.title} Page`;

      // Enrich page view events with form data on selected pages.
      const formDataEnrichmentPages = ['Responses', 'Analytics', 'Customise', 'Settings', 'Builder', 'Share'];
      const formId = getFormIdFromUrl();
      
      if (formDataEnrichmentPages.includes(page.name) && formId) {

        try {
          const customTemplates = await fplsFetch({ url: `/api/v2/user/${formId}/custom_templates` });
          const form = await fplsFetch({ url: `/api/v2/user/form/${formId}` });

          if (!form.error && form.definition) {
            const formSettingsUsed = [
              form.options?.randomise_question_order ? 'Randomise Question Order' : '',
              form?.include_response_in_autoresponder ? 'Auto Responder' : '',
              form?.notification_type !== 'off' ? 'Email Notification' : '',
              customTemplates?.templates?.length ? 'Document Merge' : '',
              form.options?.ga_tracking_id ? 'Google Analytics' : '',
              form.options?.offline_enabled ? 'Offline Enabled' : '',
              form.options?.enable_esignature ? 'E-Sign' : '',
              form.get_location ? 'Location Data' : ''
            ].filter((item) => item);

            event.event_properties.formSettingsUsed = formSettingsUsed;
            event.event_properties.formId = formId;
          }
        } catch (error) {
          console.error('Amplitude page view event form error: ', error);
        }
      }

      event.event_properties = {
        ...event.event_properties,
        '[Amplitude] Page Title': page.title
      };
    } else if (!event?.user_id) {
      // This enrichment attempts to identify or block unidentified events.
      // A scenario is an event triggered after the user is logged out.
      const userId = window?.EMAIL;
      if (!userId) return null;
      event.user_id = userId;
    }

    console.log(event.event_properties);

    return event;
  },
});

export const identifyAmplitudeUser = () => {
  const email = window?.EMAIL ?? '';
  ampli.identify(email);
};

export const loadAmplitude = () => {
  const activeEnvironment = getEnvironment();

  const configuration = {
    fetchRemoteConfig: true,
    autocapture: {
      elementInteractions: false,
      formInteractions: false,
      fileDownloads: false,
      attribution: false,
      sessions: false,
      pageViews: true
    }
  };

  const environment = {
    production: 'formplusproduction',
    development: 'formpluslocalhost',
    staging: 'formplusstaging'
  }[activeEnvironment];

  const apiKey = {
    development: '87c69de007ff2f4caa204837d229849e',
    // production: '80b0de0573e0e2290c4707defa48e66b',  //  This will be activated when remaining events have been added in v50.
    production: '0fc81a7dc85ee50e412d305f238b87f2',
    staging: '0fc81a7dc85ee50e412d305f238b87f2'
  }[activeEnvironment];
  
  const instance = createInstance();

  instance.add(trackingEnrichmentPlugin());

  instance.init(apiKey, undefined, {
    ...DefaultConfiguration,
    ...configuration
  });

  const client = { instance };
  ampli.load({ client });

  console.log('Amplitude initialized successfully!');
};

export const Ampli = ampli;